import PageBrand from '@components/common/PageBrand'
import Grid from '@mui/material/Grid'
import React, { useState } from 'react'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import { useHistory } from 'react-router-dom'
import { err, isValidEmail } from '../utils/functions'
import { sendResetPasswordEmail } from '../api/user'
import Alert, { AlertInput, handleAlert } from '../components/common/Alert'
import { GlassButton, GlassCard, GlassOutlineButton } from '@components/common/Glass'
import BackgroundAnimation from '@components/common/BackgroundAnimation'

export default function ForgotPassword(): JSX.Element {
  const theme = useTheme()
  const history = useHistory()

  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [alert, setAlert] = useState<AlertInput>({ open: false })

  const [email, setEmail] = useState<string>('')
  const inputSx = {
    '& .MuiInputBase-root': {
      background: '#ffffff',
      borderRadius: '14px',
      // border: '1px solid rgba(255,255,255,0.75)',
      boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
      backdropFilter: 'none',
    },
    '& .MuiInputBase-input': {
      py: 1.25,
    },
  } as const

  const handleSendResetLink = () => {
    if (!email) return

    // check email and password again
    if (!email || !isValidEmail(email)) {
      setAlert({
        open: true,
        severity: 'error',
        message: 'Invalid email.',
      })
      return
    }

    sendResetPasswordEmail(email)
      .then((res) => {
        handleAlert(setAlert, res, 'Email sent! Check your inbox')
      })
      .catch((e) => {
        err(e)
      })
  }

  return (
    <div>
      <BackgroundAnimation />
      <Container
        sx={{
          minHeight: '100dvh',
          py: 3,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
        }}
        maxWidth="sm"
      >
        <PageBrand />
        <GlassCard
          sx={{
            width: '100%',
            p: { xs: 1.5, sm: 3 },
            borderRadius: '32px',
            borderColor: 'rgba(255,255,255,0.7)',
            boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0}>
            <GlassOutlineButton
              variant="outlined"
              onClick={() => history.push('/login')}
              sx={{
                p: 1,
                minWidth: 'auto',
              }}
            >
              <ArrowBackIosNewRoundedIcon fontSize="small" sx={{ mr: 0.25 }} />
            </GlassOutlineButton>
          </Box>

          <form
            onSubmit={async (e) => {
              e.preventDefault()
              handleSendResetLink()
            }}
          >
            <Grid container justifyContent="center" spacing={1} mt={1}>
              <Grid item xs={12}>
                <Typography variant={fullScreen ? 'h4' : 'h4'} fontWeight={600}>
                  Forgot Password
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={1}>
                  We&apos;ll send an email to reset your password.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container justifyContent="center" spacing={2} mt={3}>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth={true}
                      sx={inputSx}
                      label="Email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required={true}
                    ></TextField>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} mt={2}>
                <GlassButton
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    minHeight: 48,
                    fontSize: 16,
                  }}
                  type="submit"
                >
                  Send Reset Link
                </GlassButton>
              </Grid>
            </Grid>
          </form>
        </GlassCard>
      </Container>
      <Alert alert={alert} onClose={() => setAlert({ ...alert, open: false })} />
    </div>
  )
}
